import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save, Send, ArrowLeft, Calculator } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Currency, CurrencyInput } from "@/components/i18n";
import { useCountry } from "@/contexts/CountryContext";
import DashboardContainer from "../layout/DashboardContainer";
import InvoiceAPIService from "@/services/invoice-api";
import {
  Invoice,
  UpdateInvoiceRequest,
  CreateInvoiceLineItemRequest,
  AvailableContract,
} from "@/types/invoice";

interface LineItemFormData {
  product: string;
  description: string;
  rate: string;
  quantity: string;
  tax: string;
}

interface InvoiceFormData {
  contractorContractId: string;
  invoiceNumber: string;
  dueDate: string;
  notes: string;
  lineItems: LineItemFormData[];
}

const LineItemRow: React.FC<{
  item: LineItemFormData;
  index: number;
  onUpdate: (
    index: number,
    field: keyof LineItemFormData,
    value: string,
  ) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}> = ({ item, index, onUpdate, onRemove, canRemove }) => {
  const { getCurrencySymbol } = useCountry();

  const calculateTotal = () => {
    const rate = parseFloat(item.rate) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    const tax = parseFloat(item.tax) || 0;
    return rate * quantity + tax;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  return (
    <Card className="p-4">
      <div className="grid gap-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Line Item {index + 1}</h4>
          {canRemove && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemove(index)}
              className="h-8 w-8 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`product-${index}`}>Product/Service *</Label>
            <Input
              id={`product-${index}`}
              value={item.product}
              onChange={(e) => onUpdate(index, "product", e.target.value)}
              placeholder="e.g., Childcare Services"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`description-${index}`}>Description</Label>
            <Input
              id={`description-${index}`}
              value={item.description}
              onChange={(e) => onUpdate(index, "description", e.target.value)}
              placeholder="Optional description"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor={`rate-${index}`}>Rate ({getCurrencySymbol()}) *</Label>
            <Input
              id={`rate-${index}`}
              type="number"
              step="0.01"
              min="0"
              value={item.rate}
              onChange={(e) => onUpdate(index, "rate", e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`quantity-${index}`}>Quantity / Hours *</Label>
            <Input
              id={`quantity-${index}`}
              type="number"
              step="0.01"
              min="0.01"
              value={item.quantity}
              onChange={(e) => onUpdate(index, "quantity", e.target.value)}
              placeholder="1.00"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`tax-${index}`}>Tax (£)</Label>
            <Input
              id={`tax-${index}`}
              type="number"
              step="0.01"
              min="0"
              value={item.tax}
              onChange={(e) => onUpdate(index, "tax", e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="space-y-2">
            <Label>Total</Label>
            <div className="flex items-center h-10 px-3 py-2 border border-input bg-muted rounded-md text-sm">
              {formatCurrency(calculateTotal())}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export function EditInvoiceForm({
  invoice,
  onCancel,
  onSuccess,
}: {
  invoice: Invoice;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [availableContracts, setAvailableContracts] = useState<
    AvailableContract[]
  >([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [formData, setFormData] = useState<InvoiceFormData>({
    contractorContractId: "",
    invoiceNumber: "",
    dueDate: "",
    notes: "",
    lineItems: [],
  });

  // Initialize form data with invoice data
  useEffect(() => {
    const lineItemsData: LineItemFormData[] = invoice.lineItems.map((item) => ({
      product: item.product,
      description: item.description || "",
      rate: item.rate.toString(),
      quantity: item.quantity.toString(),
      tax: item.tax.toString(),
    }));

    setFormData({
      contractorContractId: invoice.contractorContractId || "",
      invoiceNumber: invoice.invoiceNumber || "",
      dueDate: invoice.dueDate,
      notes: invoice.notes || "",
      lineItems:
        lineItemsData.length > 0
          ? lineItemsData
          : [
              {
                product: "",
                description: "",
                rate: "",
                quantity: "1",
                tax: "0",
              },
            ],
    });
  }, [invoice]);

  // Fetch available contracts on component mount
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await InvoiceAPIService.getAvailableContracts();
        setAvailableContracts(response.contracts);
      } catch (error) {
        console.error("Error fetching contracts:", error);
        toast({
          title: "Error",
          description: "Failed to load available contracts. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingContracts(false);
      }
    };

    fetchContracts();
  }, [toast]);

  const updateLineItem = (
    index: number,
    field: keyof LineItemFormData,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          product: "",
          description: "",
          rate: "",
          quantity: "1",
          tax: "0",
        },
      ],
    }));
  };

  const removeLineItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;

    formData.lineItems.forEach((item) => {
      const rate = parseFloat(item.rate) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const tax = parseFloat(item.tax) || 0;

      subtotal += rate * quantity;
      totalTax += tax;
    });

    return {
      subtotal,
      totalTax,
      grandTotal: subtotal + totalTax,
    };
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!formData.dueDate) {
      errors.push("Due date is required");
    } else {
      const dueDate = new Date(formData.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (dueDate <= today) {
        errors.push("Due date must be in the future");
      }
    }

    if (formData.lineItems.length === 0) {
      errors.push("At least one line item is required");
    }

    formData.lineItems.forEach((item, index) => {
      if (!item.product.trim()) {
        errors.push(`Line item ${index + 1}: Product/Service is required`);
      }

      const rate = parseFloat(item.rate);
      if (isNaN(rate) || rate <= 0) {
        errors.push(`Line item ${index + 1}: Rate must be greater than 0`);
      }

      const quantity = parseFloat(item.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        errors.push(
          `Line item ${index + 1}: Quantity / Hours must be greater than 0`,
        );
      }

      const tax = parseFloat(item.tax);
      if (isNaN(tax) || tax < 0) {
        errors.push(`Line item ${index + 1}: Tax must be 0 or greater`);
      }
    });

    const { grandTotal } = calculateTotals();
    if (grandTotal <= 0) {
      errors.push("Invoice total must be greater than £0.00");
    }

    return errors;
  };

  const handleSubmit = async (
    isDraft: boolean = false,
    shouldSubmit: boolean = false,
  ) => {
    const errors = validateForm();
    if (errors.length > 0) {
      toast({
        title: "Validation Error",
        description: errors[0],
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const lineItems: CreateInvoiceLineItemRequest[] = formData.lineItems.map(
        (item) => ({
          product: item.product.trim(),
          description: item.description.trim() || undefined,
          rate: parseFloat(item.rate),
          quantity: parseFloat(item.quantity),
          tax: parseFloat(item.tax),
        }),
      );

      const requestData: UpdateInvoiceRequest = {
        contractorContractId: formData.contractorContractId,
        dueDate: formData.dueDate,
        invoiceNumber: formData.invoiceNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        lineItems,
      };

      await InvoiceAPIService.updateInvoice(invoice.id, requestData);

      // If shouldSubmit is true, submit the invoice after updating
      if (shouldSubmit) {
        await InvoiceAPIService.submitInvoice(invoice.id);
        toast({
          title: "Success",
          description: "Invoice updated and submitted successfully.",
        });
      } else {
        toast({
          title: "Success",
          description: `Invoice ${isDraft ? "saved as draft" : "updated"} successfully.`,
        });
      }

      onSuccess();
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast({
        title: "Error",
        description: "Failed to update invoice. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const { subtotal, totalTax, grandTotal } = calculateTotals();

  const headerContent = (
    <div className="flex flex-col sm:flex-row gap-2">
      <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Invoices
      </Button>
    </div>
  );

  return (
    <DashboardContainer
      title={`Edit Invoice ${invoice.invoiceNumber || `#${invoice.id}`}`}
      headerContent={headerContent}
    >
      <div className="space-y-6">
        {/* Invoice Details */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">Invoice Number</Label>
                <Input
                  id="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      invoiceNumber: e.target.value,
                    }))
                  }
                  placeholder="e.g., INV-2024-001 (optional)"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to auto-generate
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date *</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  min={
                    new Date(Date.now() + 86400000).toISOString().split("T")[0]
                  } // Tomorrow
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Optional notes about this invoice..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Line Items</h3>
            <Button onClick={addLineItem} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Line Item
            </Button>
          </div>

          <div className="space-y-4">
            {formData.lineItems.map((item, index) => (
              <LineItemRow
                key={index}
                item={item}
                index={index}
                onUpdate={updateLineItem}
                onRemove={removeLineItem}
                canRemove={formData.lineItems.length > 1}
              />
            ))}
          </div>
        </div>

        {/* Invoice Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Invoice Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Tax:</span>
                <span>{formatCurrency(totalTax)}</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between text-lg font-medium">
                  <span>Total:</span>
                  <span>{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6">
          <Button
            onClick={() => handleSubmit(true)}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? "Saving as draft..." : "Save as Draft"}
          </Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? "Updating..." : "Update Invoice"}
          </Button>
          <Button
            onClick={() => handleSubmit(false, true)}
            disabled={isLoading}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
          >
            <Send className="h-4 w-4 mr-2" />
            {isLoading ? "Updating & Submitting..." : "Update & Submit"}
          </Button>
        </div>
      </div>
    </DashboardContainer>
  );
}
